import { Box, Button as MuiButton } from '@mui/material';
import { useMemo } from 'react';
import { r, s, spacing } from '../../theme/tokens';
import { CircularProgress } from './CircularProgress';
import { Icon } from './Icon';
import type { ButtonProps as MuiButtonProps } from '@mui/material/Button';
import type { ElementType, ReactElement, ReactNode } from 'react';

type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'destructive'
  | 'contained'
  | 'outlined'
  | 'text';
type ButtonSize = 'sm' | 'md' | 'lg' | 'small' | 'medium' | 'large';
type ButtonVisualSize = 'sm' | 'md' | 'lg';

export type ButtonProps<C extends ElementType = 'button'> = Omit<
  MuiButtonProps<C>,
  'variant' | 'size' | 'startIcon' | 'endIcon' | 'loading'
> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidthOnMobile?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  to?: string;
};

const sizeStyles: Record<
  ButtonVisualSize,
  { paddingY: string; paddingX: string; fontSize: string }
> = {
  sm: { paddingY: s('xs'), paddingX: s('md'), fontSize: '15px' },
  md: { paddingY: s('sm'), paddingX: s('lg'), fontSize: '15px' },
  lg: { paddingY: s('md'), paddingX: s('xl'), fontSize: '15px' },
};

export const Button = <C extends ElementType = 'button'>({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidthOnMobile = true,
  color,
  disabled,
  children,
  startIcon,
  endIcon,
  sx,
  ...rest
}: ButtonProps<C>): ReactElement => {
  const mappedVariant = useMemo(() => {
    if (variant === 'secondary') return 'outlined';
    if (variant === 'ghost') return 'text';
    if (variant === 'destructive') return 'contained';
    if (variant === 'primary') return 'contained';
    return variant;
  }, [variant]) as MuiButtonProps['variant'];
  const mappedColor: MuiButtonProps['color'] = variant === 'destructive' ? 'error' : color;

  const resolvedSize: ButtonVisualSize =
    size === 'small' ? 'sm' : size === 'large' ? 'lg' : size === 'medium' ? 'md' : size;
  const style = sizeStyles[resolvedSize];
  const isSecondary = mappedVariant === 'outlined';
  const isDestructive = variant === 'destructive';
  const isDisabled = disabled === true || loading;

  const iconSize = resolvedSize;

  const mergedSx = [
    {
      borderRadius: r('md'),
      textTransform: 'none',
      fontWeight: 600,
      fontSize: style.fontSize,
      height: '32px',
      minHeight: '32px',
      paddingY: style.paddingY,
      paddingX: style.paddingX,
      position: 'relative',
      width: fullWidthOnMobile ? { xs: '100%', sm: 'auto' } : 'auto',
      minWidth: 'auto',
      ...(loading ? { minWidth: '6.5rem' } : {}),
      borderColor: isSecondary ? 'primary.main' : undefined,
      '&:focus-visible': { outline: '2px solid currentColor', outlineOffset: s('xs') },
    },
    isDestructive && !isDisabled
      ? {
          '&&': { color: 'common.white' },
          '&&:hover': { color: 'common.white' },
        }
      : undefined,
    ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
  ].filter(Boolean) as MuiButtonProps<C>['sx'];

  const loadingLabel = children ?? 'Loading';

  return (
    <MuiButton
      variant={mappedVariant}
      color={mappedColor}
      disabled={isDisabled}
      startIcon={startIcon ? <Icon size={iconSize}>{startIcon}</Icon> : undefined}
      endIcon={endIcon ? <Icon size={iconSize}>{endIcon}</Icon> : undefined}
      sx={mergedSx}
      {...rest}
    >
      {loading ? (
        <>
          <Box component="span" sx={{ opacity: 0 }}>
            {loadingLabel}
          </Box>
          <Box
            component="span"
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={spacing.lg} color="inherit" />
          </Box>
        </>
      ) : (
        children
      )}
    </MuiButton>
  );
};
